import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PayActions } from './PayActions';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Loader2, Plus, Pencil, Trash2, FileText, Mail, Download,
  CheckCircle2, Briefcase, Copy, X, ShieldCheck, GripVertical,
  Phone, MapPin, User as UserIcon, Star,
} from 'lucide-react';
import { useConfirm } from './ConfirmDialog';
import RichTextEditor from './RichTextEditor';
import { SupplierHireDialog } from './SupplierHireDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  draft: 'bg-slate-500/20 text-slate-300 border-slate-500/40',
  sent: 'bg-amber-500/20 text-amber-400 border-amber-500/40',
  accepted: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40',
  completed: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/40',
  cancelled: 'bg-red-500/20 text-red-400 border-red-500/40',
};

function emptyForm() {
  return {
    business_name: '',
    business_address: '',
    business_phone: '',
    business_email: '',
    contacts: [{ first_name: '', last_name: '', email: '', phone: '', notes: '', is_primary: true }],
    responsibilities: '',
    vendor_fee_cents: 0,
    status: 'draft',
    notes: '',
  };
}

export default function EventVendorsPanel({ workspaceId, eventId, token }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventCity, setEventCity] = useState(null);
  const [editing, setEditing] = useState(null); // ev_id or 'new'
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [contractLinks, setContractLinks] = useState({});
  const [contractEditor, setContractEditor] = useState(null); // {ev_id, heading, sections, is_signed} | null
  const [contractSaving, setContractSaving] = useState(false);
  const [hireOpen, setHireOpen] = useState(false);
  const [hiring, setHiring] = useState(false);
  const [bookings, setBookings] = useState([]);
  const confirm = useConfirm();

  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const parentPath = workspaceId ? `workspaces/${workspaceId}` : `events/${eventId}`;

  useEffect(() => {
    if (!eventId) return;
    axios.get(`${API}/hosted-events/${eventId}`, auth)
      .then(r => setEventCity(r.data?.city || r.data?.event?.city || null))
      .catch(() => {});
    loadBookings();
    const params = new URLSearchParams(window.location.search);
    const paidId = params.get('booking_paid');
    const phase = params.get('phase');
    if (paidId && phase) {
      axios.get(`${API}/service-bookings/${paidId}/confirm`, { params: { phase } })
        .then(r => {
          if (r.data.ok) toast.success(phase === 'deposit' ? 'Deposit paid — booking locked in 🔒' : 'Paid in full — vendor payout scheduled after the event');
          loadBookings();
          params.delete('booking_paid'); params.delete('phase');
          window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
        })
        .catch(() => {});
    }
    // eslint-disable-next-line
  }, [eventId]);

  const loadBookings = async () => {
    if (!eventId) return;
    try {
      const r = await axios.get(`${API}/events/${eventId}/service-bookings`, auth);
      setBookings(r.data.bookings || []);
    } catch { /* non-blocking */ }
  };

  const payBooking = async (bookingId, phase) => {
    try {
      const r = await axios.post(`${API}/service-bookings/${bookingId}/pay`, { phase }, auth);
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start payment'); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/${parentPath}/event-vendors`, auth);
      setRows(r.data.event_vendors || []);
    } catch (e) {
      toast.error('Failed to load event vendors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspaceId, eventId]);

  const openNew = () => { setForm(emptyForm()); setEditing('new'); };
  const openEdit = (row) => {
    setForm({
      business_name: row.business_name || '',
      business_address: row.business_address || '',
      business_phone: row.business_phone || '',
      business_email: row.business_email || '',
      contacts: (row.contacts || []).length ? row.contacts : [{ first_name: '', last_name: '', email: '', phone: '', notes: '', is_primary: true }],
      responsibilities: row.responsibilities || '',
      vendor_fee_cents: row.vendor_fee_cents || 0,
      status: row.status || 'draft',
      notes: row.notes || '',
    });
    setEditing(row.ev_id);
  };
  const close = () => { setEditing(null); setForm(emptyForm()); };

  const updateContact = (i, k, v) => {
    setForm((f) => {
      const next = [...f.contacts];
      next[i] = { ...next[i], [k]: v };
      if (k === 'is_primary' && v) next.forEach((c, idx) => { if (idx !== i) c.is_primary = false; });
      return { ...f, contacts: next };
    });
  };
  const addContact = () => setForm((f) => ({ ...f, contacts: [...f.contacts, { first_name: '', last_name: '', email: '', phone: '', notes: '', is_primary: false }] }));
  const removeContact = (i) => setForm((f) => ({ ...f, contacts: f.contacts.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.business_name.trim()) { toast.error('Business name required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, vendor_fee_cents: Math.round(parseFloat(form.vendor_fee_cents || 0)) };
      if (editing === 'new') {
        await axios.post(`${API}/${parentPath}/event-vendors`, payload, auth);
        toast.success('Vendor record created & synced to CRM');
      } else {
        await axios.put(`${API}/event-vendors/${editing}`, payload, auth);
        toast.success('Vendor record updated & CRM synced');
      }
      close();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (row) => {
    const ok = await confirm({
      title: `Delete "${row.business_name || 'this vendor'}"?`,
      body: `This permanently removes the vendor record, any generated contract, and the synced CRM lead. This cannot be undone.`,
      confirmText: 'Delete vendor',
      cancelText: 'Cancel',
      destructive: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/event-vendors/${row.ev_id}`, auth);
      toast.success(`Deleted ${row.business_name || 'vendor'}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const generateContract = async (row) => {
    try {
      const r = await axios.post(`${API}/event-vendors/${row.ev_id}/contract/generate`, {}, auth);
      const url = r.data.public_url;
      setContractLinks((s) => ({ ...s, [row.ev_id]: url }));
      toast.success('Contract generated');
      load();
    } catch (e) { toast.error('Failed to generate contract'); }
  };

  const sendPortal = async (row) => {
    if (!row.business_email && !(row.contacts || []).some((c) => c.email)) {
      toast.error('No vendor email on file');
      return;
    }
    try {
      const r = await axios.post(`${API}/event-vendors/${row.ev_id}/portal-invite`, {}, auth);
      toast.success('Portal invite sent');
      setContractLinks((s) => ({ ...s, [`${row.ev_id}_portal`]: r.data.portal_url }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send'); }
  };

  const copyLink = (url) => {
    navigator.clipboard?.writeText(url);
    toast.success('Link copied');
  };

  const openContractEditor = async (row) => {
    try {
      const r = await axios.get(`${API}/event-vendors/${row.ev_id}/contract`, auth);
      setContractEditor({
        ev_id: row.ev_id,
        heading: r.data.heading || 'Eventism Vendor Service Agreement',
        sections: (r.data.sections || []).map((s) => ({ ...s })),
        is_signed: !!r.data.is_signed,
        public_token: r.data.public_token,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load contract');
    }
  };
  const closeContractEditor = () => setContractEditor(null);
  const updateSection = (idx, k, v) => {
    setContractEditor((c) => {
      const next = [...c.sections];
      next[idx] = { ...next[idx], [k]: v };
      return { ...c, sections: next };
    });
  };
  const addSection = () => setContractEditor((c) => ({ ...c, sections: [...c.sections, { id: `new_${Date.now()}`, title: 'New Section', body: '' }] }));
  const removeSection = (idx) => setContractEditor((c) => ({ ...c, sections: c.sections.filter((_, i) => i !== idx) }));
  const moveSection = (idx, dir) => setContractEditor((c) => {
    const next = [...c.sections];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return c;
    [next[idx], next[target]] = [next[target], next[idx]];
    return { ...c, sections: next };
  });
  const saveContract = async () => {
    if (!contractEditor.sections.length) { toast.error('Add at least one section'); return; }
    setContractSaving(true);
    try {
      await axios.put(`${API}/event-vendors/${contractEditor.ev_id}/contract`, {
        heading: contractEditor.heading,
        sections: contractEditor.sections,
      }, auth);
      toast.success('Contract updated');
      closeContractEditor();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setContractSaving(false); }
  };

  return (
    <Card data-testid="event-vendors-panel">
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2"><Briefcase className="w-5 h-5 text-indigo-500" /> Event Vendors</CardTitle>
          <CardDescription>Rich vendor records with contacts, responsibilities, fees, contracts, and insurance — auto-synced to your CRM.</CardDescription>
        </div>
        <Button onClick={openNew} data-testid="add-event-vendor-btn"><Plus className="w-4 h-4 mr-1" /> Add vendor</Button>
      </CardHeader>
      <CardContent>
        <SupplierHireDialog open={hireOpen} onClose={() => setHireOpen(false)} city={eventCity} requesting={hiring}
          onRequest={async (payload) => {
            setHiring(true);
            try {
              await axios.post(`${API}/events/${eventId}/service-bookings`, payload, auth);
              toast.success("Request sent — we'll email you the moment the vendor responds");
              setHireOpen(false);
              loadBookings();
            } catch (e) { toast.error(e.response?.data?.detail || 'Could not send request'); }
            finally { setHiring(false); }
          }} />
        {eventId && (
          <div className="flex items-center justify-between gap-3 rounded-xl border border-fuchsia-500/30 bg-fuchsia-500/5 px-4 py-3 mb-4 flex-wrap">
            <div>
              <p className="text-sm font-semibold">Need a DJ, caterer or photographer?</p>
              <p className="text-xs text-muted-foreground">Request a Supplier Hub package — the vendor confirms, a 50% deposit locks the date, Eventism handles the contract & payout.</p>
            </div>
            <Button size="sm" className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
              onClick={() => setHireOpen(true)} data-testid="browse-suppliers-link">
              Find & Hire Local Vendors
            </Button>
          </div>
        )}
        {bookings.length > 0 && (
          <div className="space-y-2 mb-5" data-testid="marketplace-bookings">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Marketplace bookings</p>
            {bookings.map(bk => {
              const total = (bk.customer_total_cents || 0) / 100;
              const dep = (bk.deposit_cents || 0) / 100;
              const bal = (bk.balance_cents || 0) / 100;
              const chip = {
                requested: ['Awaiting vendor', 'bg-amber-500/15 text-amber-400 border-amber-500/30'],
                accepted: ['Accepted — deposit due', 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30'],
                declined: ['Vendor declined', 'bg-rose-500/15 text-rose-400 border-rose-500/30'],
                locked: ['Locked — balance due', 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30'],
                paid: ['Paid in full', 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'],
              }[bk.status] || [bk.status, 'bg-muted text-muted-foreground border-border'];
              return (
                <div key={bk.booking_id} className="rounded-xl border border-border p-3 flex items-center gap-3 flex-wrap"
                  data-testid={`booking-${bk.booking_id}`}>
                  {bk.image_url && <img src={bk.image_url} alt="" className="h-10 w-14 object-cover rounded-md hidden sm:block" />}
                  <div className="flex-1 min-w-[180px]">
                    <p className="text-sm font-semibold">{bk.service_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {bk.host?.business_name}{bk.contact_locked ? ' · contact unlocks on acceptance' : bk.host?.email ? ` · ${bk.host.email}` : ''}
                    </p>
                    <p className="text-xs text-muted-foreground">Total ${total.toFixed(2)} · deposit ${dep.toFixed(2)} · balance ${bal.toFixed(2)} due {bk.balance_due_date}</p>
                    {bk.vendor_message && <p className="text-[11px] text-muted-foreground italic mt-0.5">"{bk.vendor_message}"</p>}
                  </div>
                  <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${chip[1]}`} data-testid={`booking-status-${bk.booking_id}`}>{chip[0]}</span>
                  {bk.status === 'accepted' && (
                    <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white h-8 text-xs"
                      onClick={() => payBooking(bk.booking_id, 'deposit')} data-testid={`pay-deposit-${bk.booking_id}`}>
                      Pay ${dep.toFixed(2)} deposit
                    </Button>
                  )}
                  {bk.status === 'locked' && (
                    <Button size="sm" className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white h-8 text-xs"
                      onClick={() => payBooking(bk.booking_id, 'balance')} data-testid={`pay-balance-${bk.booking_id}`}>
                      Pay ${bal.toFixed(2)} balance
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {loading ? (
          <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-400" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">No event-vendor records yet. Click "Add vendor" to capture full vendor info, generate a contract, and send a portal link.</div>
        ) : (
          <div className="space-y-3">
            {rows.map((row) => (
              <div key={row.ev_id} className="border rounded-lg p-4" data-testid={`event-vendor-${row.ev_id}`}>
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div className="flex-1 min-w-[260px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold">{row.business_name || <span className="italic text-muted-foreground">(no business name)</span>}</h4>
                      <Badge variant="outline" className={STATUS_COLORS[row.status] || ''}>{row.status}</Badge>
                      {row.contract?.signed_at && (
                        <Badge variant="outline" className="bg-emerald-500/20 text-emerald-400 border-emerald-500/40"><CheckCircle2 className="w-3 h-3 mr-1" /> Signed</Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mt-1.5">
                      {row.business_email && (
                        <a href={`mailto:${row.business_email}`} className="inline-flex items-center gap-1 hover:text-indigo-500 hover:underline" data-testid={`row-biz-email-${row.ev_id}`}>
                          <Mail className="w-3 h-3" /> {row.business_email}
                        </a>
                      )}
                      {row.business_phone && (
                        <a href={`tel:${row.business_phone}`} className="inline-flex items-center gap-1 hover:text-indigo-500 hover:underline" data-testid={`row-biz-phone-${row.ev_id}`}>
                          <Phone className="w-3 h-3" /> {row.business_phone}
                        </a>
                      )}
                      {row.business_address && (
                        <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {row.business_address}</span>
                      )}
                    </div>
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground mt-2">{(row.contacts || []).length} contact{(row.contacts || []).length === 1 ? '' : 's'} · Fee: ${((row.vendor_fee_cents || 0) / 100).toFixed(2)}</p>
                    {(row.contacts || []).length > 0 && (
                      <div className="mt-1.5 grid sm:grid-cols-2 gap-1.5">
                        {(row.contacts || []).map((c, ci) => {
                          const fullName = `${c.first_name || ''} ${c.last_name || ''}`.trim() || '(unnamed)';
                          return (
                            <div key={ci} className="rounded-md bg-muted/40 border border-border/60 p-2 text-xs" data-testid={`row-contact-${row.ev_id}-${ci}`}>
                              <div className="flex items-center gap-1.5 font-medium">
                                {c.is_primary ? <Star className="w-3 h-3 text-amber-400 fill-amber-400" /> : <UserIcon className="w-3 h-3 text-muted-foreground" />}
                                <span>{fullName}</span>
                              </div>
                              <div className="flex flex-col gap-0.5 mt-1">
                                {c.email && (
                                  <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 text-indigo-400 hover:text-indigo-500 hover:underline" data-testid={`row-contact-email-${row.ev_id}-${ci}`}>
                                    <Mail className="w-3 h-3" /> {c.email}
                                  </a>
                                )}
                                {c.phone && (
                                  <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 text-fuchsia-400 hover:text-fuchsia-500 hover:underline" data-testid={`row-contact-phone-${row.ev_id}-${ci}`}>
                                    <Phone className="w-3 h-3" /> {c.phone}
                                  </a>
                                )}
                                {c.notes && <span className="text-muted-foreground italic mt-0.5">{c.notes}</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {eventId && (
                      <PayActions eventId={eventId} payeeType="vendor" refId={row.ev_id} name={row.business_name}
                        email={row.business_email} agreedCents={row.vendor_fee_cents} paidCents={row.paid_cents} onDone={load} />
                    )}
                    <Button size="sm" variant="outline" onClick={() => openEdit(row)} data-testid={`edit-event-vendor-${row.ev_id}`}><Pencil className="w-4 h-4 mr-1" /> Edit</Button>
                    <Button size="sm" variant="outline" onClick={() => generateContract(row)} data-testid={`gen-contract-${row.ev_id}`}>
                      <FileText className="w-4 h-4 mr-1" /> {row.contract ? 'Regenerate contract' : 'Generate contract'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => sendPortal(row)} data-testid={`send-portal-${row.ev_id}`}><Mail className="w-4 h-4 mr-1" /> Send portal</Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => remove(row)} data-testid={`delete-event-vendor-${row.ev_id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
                {(row.contract?.public_token || contractLinks[row.ev_id]) && (
                  <div className="mt-3 rounded-md bg-indigo-500/5 border border-indigo-500/30 p-2 flex items-center justify-between text-xs flex-wrap gap-2">
                    <div className="flex items-center gap-2"><FileText className="w-3.5 h-3.5 text-indigo-500" /> Contract link ready{row.contract?.signed_at ? ' · signed' : ''}</div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {!row.contract?.signed_at && (
                        <Button size="sm" variant="ghost" onClick={() => openContractEditor(row)} data-testid={`edit-contract-${row.ev_id}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Edit contract</Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => copyLink(contractLinks[row.ev_id] || `${window.location.origin}/contract/${row.contract.public_token}`)}><Copy className="w-3.5 h-3.5 mr-1" /> Copy link</Button>
                      <a className="text-indigo-600 hover:underline inline-flex items-center gap-1" href={`/contract/${row.contract?.public_token}`} target="_blank" rel="noreferrer">Open</a>
                      <a className="text-emerald-600 hover:underline inline-flex items-center gap-1" href={`${API}/contracts/public/${row.contract?.public_token}/download`}><Download className="w-3.5 h-3.5" /> Download</a>
                    </div>
                  </div>
                )}
                {row.portal_token && (
                  <div className="mt-2 rounded-md bg-fuchsia-500/5 border border-fuchsia-500/30 p-2 flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-fuchsia-500" /> Vendor portal link</div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="ghost" onClick={() => copyLink(`${window.location.origin}/vendor-portal/${row.portal_token}`)}><Copy className="w-3.5 h-3.5 mr-1" /> Copy link</Button>
                      <a className="text-fuchsia-600 hover:underline" href={`/vendor-portal/${row.portal_token}`} target="_blank" rel="noreferrer">Open</a>
                    </div>
                  </div>
                )}
                {row.insurance_filename && (
                  <div className="mt-2 text-xs text-emerald-600 flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5" /> Insurance on file: {row.insurance_filename}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={!!editing} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Add event vendor' : 'Edit event vendor'}</DialogTitle>
            <DialogDescription>Full vendor info, contacts, responsibilities, and fee. Saves to CRM automatically.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Business name *</label>
                <Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="form-biz-name" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Main email</label>
                <Input type="email" value={form.business_email} onChange={(e) => setForm({ ...form, business_email: e.target.value })} data-testid="form-biz-email" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Main phone</label>
                <Input value={form.business_phone} onChange={(e) => setForm({ ...form, business_phone: e.target.value })} data-testid="form-biz-phone" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Address</label>
                <Input value={form.business_address} onChange={(e) => setForm({ ...form, business_address: e.target.value })} data-testid="form-biz-address" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-sm">Contacts</h4>
                <Button size="sm" variant="outline" onClick={addContact} data-testid="form-add-contact"><Plus className="w-3.5 h-3.5 mr-1" /> Add contact</Button>
              </div>
              <div className="space-y-2">
                {form.contacts.map((c, i) => (
                  <div key={i} className="border rounded-md p-3 space-y-2" data-testid={`form-contact-${i}`}>
                    <div className="grid sm:grid-cols-2 gap-2">
                      <Input placeholder="First name" value={c.first_name || ''} onChange={(e) => updateContact(i, 'first_name', e.target.value)} />
                      <Input placeholder="Last name" value={c.last_name || ''} onChange={(e) => updateContact(i, 'last_name', e.target.value)} />
                      <Input type="email" placeholder="Email" value={c.email || ''} onChange={(e) => updateContact(i, 'email', e.target.value)} />
                      <Input placeholder="Phone" value={c.phone || ''} onChange={(e) => updateContact(i, 'phone', e.target.value)} />
                    </div>
                    <Textarea placeholder="Notes" rows={2} value={c.notes || ''} onChange={(e) => updateContact(i, 'notes', e.target.value)} />
                    <div className="flex items-center justify-between text-xs">
                      <label className="inline-flex items-center gap-2">
                        <input type="checkbox" checked={!!c.is_primary} onChange={(e) => updateContact(i, 'is_primary', e.target.checked)} />
                        Primary contact
                      </label>
                      <Button size="sm" variant="ghost" onClick={() => removeContact(i)}><X className="w-3.5 h-3.5 mr-1" /> Remove</Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase text-muted-foreground">Vendor responsibilities (one per line)</label>
              <Textarea rows={4} value={form.responsibilities} onChange={(e) => setForm({ ...form, responsibilities: e.target.value })} placeholder="Deliver setup by 4pm
Provide 2 servers on site
Tear down by midnight" data-testid="form-responsibilities" />
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Vendor fee (cents)</label>
                <Input type="number" min="0" value={form.vendor_fee_cents} onChange={(e) => setForm({ ...form, vendor_fee_cents: e.target.value })} data-testid="form-fee" />
                <p className="text-[10px] text-muted-foreground mt-1">${(parseFloat(form.vendor_fee_cents || 0) / 100).toFixed(2)} USD</p>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Status</label>
                <select className="w-full bg-background border border-border rounded-md h-9 px-2 text-sm" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="form-status">
                  <option value="draft">draft</option>
                  <option value="sent">sent</option>
                  <option value="accepted">accepted</option>
                  <option value="completed">completed</option>
                  <option value="cancelled">cancelled</option>
                </select>
              </div>
              <div className="sm:col-span-1">
                <label className="text-xs font-semibold uppercase text-muted-foreground">Internal notes</label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="form-notes" />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={close}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="form-save-event-vendor">
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Save vendor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!contractEditor} onOpenChange={(o) => !o && closeContractEditor()}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="contract-editor-dialog">
          <DialogHeader>
            <DialogTitle>Edit contract</DialogTitle>
            <DialogDescription>
              Edit the heading and individual sections. The signing link stays the same — the vendor will see the latest version when they open it.
            </DialogDescription>
          </DialogHeader>

          {contractEditor && (
            <div className="space-y-4 py-2">
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Contract heading</label>
                <Input
                  value={contractEditor.heading}
                  onChange={(e) => setContractEditor((c) => ({ ...c, heading: e.target.value }))}
                  data-testid="contract-heading-input"
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold">Sections</h4>
                  <Button size="sm" variant="outline" onClick={addSection} data-testid="add-section-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add section</Button>
                </div>
                {contractEditor.sections.map((sec, i) => (
                  <div key={sec.id || i} className="border rounded-md p-3 space-y-2 bg-muted/30" data-testid={`contract-section-${i}`}>
                    <div className="flex items-center gap-2">
                      <GripVertical className="w-4 h-4 text-muted-foreground" />
                      <Input
                        value={sec.title}
                        onChange={(e) => updateSection(i, 'title', e.target.value)}
                        placeholder="Section title"
                        data-testid={`section-title-${i}`}
                      />
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => moveSection(i, -1)} disabled={i === 0} title="Move up">↑</Button>
                        <Button size="sm" variant="ghost" onClick={() => moveSection(i, 1)} disabled={i === contractEditor.sections.length - 1} title="Move down">↓</Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => removeSection(i)} data-testid={`remove-section-${i}`} title="Delete section"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                    <div data-testid={`section-rte-${i}`}>
                      <RichTextEditor
                        value={sec.body || ''}
                        onChange={(html) => updateSection(i, 'body', html)}
                        minHeight={140}
                        placeholder="Bold, italics, lists, links — format this section however you need."
                      />
                    </div>
                  </div>
                ))}
                {contractEditor.sections.length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-4">No sections yet. Click "Add section" to start.</p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeContractEditor}>Cancel</Button>
            <Button onClick={saveContract} disabled={contractSaving} data-testid="save-contract-btn">
              {contractSaving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Save contract
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
